/**
 * CertificateView — reusable renderer for a Lexia School certificate.
 *
 * Editorial style: paper background, serif hierarchy, terracotta ribbon,
 * subtle border. Rendered directly in HTML so it stays crisp on retina AND
 * can be exported to PNG via html2canvas OR printed to PDF via the
 * browser's built-in print dialog (window.print with a print-scoped stylesheet).
 */
import { forwardRef, useMemo } from "react";
import { Award, Sparkles, ShieldCheck } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

const CertificateView = forwardRef(function CertificateView({ certificate }, ref) {
    const dateFr = useMemo(() => formatDate(certificate.issued_at), [certificate.issued_at]);
    // Language may already include the variant (e.g. "anglais (américain)").
    // Only append `(variant)` when it is not already present.
    const languageAlreadyHasVariant = /\(/.test(certificate.language_fr || "");
    const variantLabel = certificate.variant && !languageAlreadyHasVariant
        ? ` (${certificate.variant})`
        : "";

    return (
        <div
            ref={ref}
            data-testid="certificate-view"
            className="relative w-full max-w-[860px] aspect-[1200/840] bg-paper border border-lexiBorder rounded-3xl overflow-hidden shadow-sm"
            style={{ containerType: "inline-size" }}
        >
            {/* Left terracotta ribbon */}
            <div className="absolute inset-y-0 left-0 w-3 bg-terracotta" />

            {/* Watermark */}
            <div className="absolute right-8 top-8 text-[10px] tracking-[0.32em] uppercase text-inkSoft">
                Lexia School · {certificate.language_iso}
            </div>

            <div className="p-10 md:p-14 h-full flex flex-col">
                <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-terracotta/15 flex items-center justify-center">
                        <Award className="w-6 h-6 text-terracotta" strokeWidth={1.75} />
                    </div>
                    <div>
                        <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft">
                            Certificat de complétion
                        </p>
                        <p className="font-serif text-lg mt-1" data-testid="certificate-track-title">
                            Parcours professionnel · {certificate.track_title_fr}
                        </p>
                    </div>
                </div>

                <div className="mt-10 md:mt-14">
                    <p className="text-sm text-inkSoft">Ce certificat atteste que</p>
                    <p
                        className="mt-3 font-serif text-4xl md:text-5xl text-ink leading-tight"
                        data-testid="certificate-first-name"
                    >
                        {certificate.first_name}
                    </p>
                    <p className="mt-5 text-base text-ink max-w-xl">
                        a terminé l&apos;intégralité des {certificate.modules_total} modules du parcours
                        {" "}
                        <span className="font-medium">{certificate.track_title_fr}</span> en
                        {" "}
                        <span className="font-medium" data-testid="certificate-language">
                            {certificate.language_fr}
                            {variantLabel}
                        </span>
                        {" "}
                        au niveau CECRL estimé
                        {" "}
                        <span className="font-medium" data-testid="certificate-level">
                            {certificate.level}
                        </span>.
                    </p>
                </div>

                <div className="mt-auto grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-lexiBorder">
                    <div>
                        <p className="text-[10px] tracking-[0.28em] uppercase text-inkSoft">Délivré le</p>
                        <p className="mt-1 font-serif text-lg" data-testid="certificate-date">{dateFr}</p>
                    </div>
                    <div>
                        <p className="text-[10px] tracking-[0.28em] uppercase text-inkSoft">Modules validés</p>
                        <p className="mt-1 font-serif text-lg">
                            {certificate.modules_completed} / {certificate.modules_total}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] tracking-[0.28em] uppercase text-inkSoft">Identifiant</p>
                        <p className="mt-1 font-mono text-[11px] text-ink truncate" data-testid="certificate-id">
                            {certificate.cert_id}
                        </p>
                    </div>
                </div>

                <p className="mt-6 text-[11px] text-inkSoft italic inline-flex items-start gap-2" data-testid="certificate-disclaimer">
                    <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-inkSoft shrink-0" />
                    {certificate.disclaimer_fr}
                </p>
            </div>
        </div>
    );
});

export default CertificateView;
